import { ProductNotification } from '../../modules/pos/quotes/entities/products.class';
import { TooltipNotification } from './tooltip-notification.class';

export const PRODUCT_NOTIFICATION_ACTIONS = {
  cancel: 'cancel',
  ok: 'ok',
  openCurrent: 'openCurrent',
  openSupersede: 'openSupersede'
} as const;
export type ProductNotificationAction = typeof PRODUCT_NOTIFICATION_ACTIONS[keyof typeof PRODUCT_NOTIFICATION_ACTIONS];

const NOTIFICATION_MESSAGE_CODES = {
  notFound: 'NotFound',
  discontinuedSuperseded: 'DiscontinuedSuperseded',
  restrictedSuperseded: 'RestrictedSuperseded',
  superseded: 'Superseded'
} as const;

const SUPERSEDE_BUTTONS: ProductNotificationAction[] = [
  PRODUCT_NOTIFICATION_ACTIONS.cancel,
  PRODUCT_NOTIFICATION_ACTIONS.openCurrent,
  PRODUCT_NOTIFICATION_ACTIONS.openSupersede
];

export const DEFAULT_PRODUCT_NOTIFICATION_BUTTONS: ProductNotificationAction[] = [
  PRODUCT_NOTIFICATION_ACTIONS.cancel,
  PRODUCT_NOTIFICATION_ACTIONS.ok
];

/**
 * PDP notification buttons keyed by NotificationMessageCode name. Unmapped codes use DEFAULT_PRODUCT_NOTIFICATION_BUTTONS.
 */
const PRODUCT_NOTIFICATION_BUTTONS = new Map<string, ProductNotificationAction[]>([
  [NOTIFICATION_MESSAGE_CODES.discontinuedSuperseded, SUPERSEDE_BUTTONS],
  [NOTIFICATION_MESSAGE_CODES.restrictedSuperseded, SUPERSEDE_BUTTONS],
  [NOTIFICATION_MESSAGE_CODES.superseded, SUPERSEDE_BUTTONS],
  [NOTIFICATION_MESSAGE_CODES.notFound, [PRODUCT_NOTIFICATION_ACTIONS.cancel]]
]);

/** NotificationMessageCode id the quote tooltip renders as a promotion (PromoPrice) */
export const PROMO_PRICE_MESSAGE_CODE_ID = 12;

const NOTIFICATION_GROUPS: string[] = ['error', 'warning', 'info'];

const compareByPriority = (a: ProductNotification, b: ProductNotification) => {
  if (a.priority < b.priority) {
    return -1;
  }
  if (a.priority > b.priority) {
    return 1;
  }
  return 0;
};

/**
 * Checks that the notification message type maps to a tooltip group (error, warning or info).
 * Used by the PDP only: the quote keeps throwing on unknown types.
 * @param notification - The product notification
 * @param messageTypes - NotificationMessageType template values (id to name)
 */
export const hasKnownMessageType = (notification: ProductNotification, messageTypes: Record<number, string>): boolean =>
  NOTIFICATION_GROUPS.includes(messageTypes[notification.messageTypeId]?.toLowerCase());

/**
 * Groups notifications into error, warning and info, sorted by priority. When there is an error, only the top priority error is kept.
 * @param notifications - The product notifications
 * @param messageTypes - NotificationMessageType template values (id to name)
 */
export const prioritizeNotifications = (
  notifications: ProductNotification[],
  messageTypes: Record<number, string>
): TooltipNotification => {
  const grouped = new TooltipNotification();
  (notifications ?? []).forEach(notice => {
    const key = messageTypes[notice.messageTypeId].toLowerCase();
    grouped[key].push(notice);
  });
  grouped.error.sort(compareByPriority);
  if (grouped.error.length) {
    return { error: [grouped.error[0]], warning: [], info: [] };
  }
  grouped.warning.sort(compareByPriority);
  grouped.info.sort(compareByPriority);
  return grouped;
};

/**
 * Display notification categories by priority.
 * @param tooltipNotification - The prioritized notifications
 */
export const getDisplayedNotifications = (tooltipNotification: TooltipNotification): ProductNotification[] => {
  if (tooltipNotification.error.length) {
    return tooltipNotification.error;
  }
  return tooltipNotification.warning.length ? tooltipNotification.warning : tooltipNotification.info;
};

/**
 * Gets the PDP notification buttons for a notification
 * @param notification - The highest priority displayed notification
 * @param messageCodes - NotificationMessageCode template values (id to name)
 */
export const getProductNotificationButtons = (
  notification: ProductNotification,
  messageCodes: Record<number, string>
): ProductNotificationAction[] =>
  PRODUCT_NOTIFICATION_BUTTONS.get(messageCodes[notification.messageCodeId]) ?? DEFAULT_PRODUCT_NOTIFICATION_BUTTONS;
